import React, { useState, useEffect, useCallback, useRef } from "react";
import { Building2 } from "lucide-react";
import { encodeRefNum } from "../utils/refNumCrypto";
import { withCountryParam } from "../utils/caseHref";
import {
  getPropertyAssessmentList,
  getPropertyClientDemo,
  getPropertyClientConfig,
  getPropertyUseridList,
} from "../services/propertyApiService";
import {
  toPropertyList,
  filterRowsByCountry,
} from "../services/adapters/propertyAssessment";
import { getStatusInfo, formatDateTime } from "../utils/statusMapping";

import latviaFlag from "../assets/icons/cases/latvia-flag.svg";
import lithuaniaFlag from "../assets/icons/cases/lithuania-flag.svg";
import estoniaFlag from "../assets/icons/cases/estonia-flag.svg";
import loadingIcon from "../assets/icons/cases/loading.svg";
import chevronDownFigma from "../assets/icons/cases/chevron-down-figma.svg";
import searchIcon from "../assets/icons/cases/search.svg";

// Sibling to Cases.js, not a branch inside it. The property list page calls
// exactly four endpoints (see propertyApiService.js): /assessment_list/all,
// /client_demo, /client_config and /useridList — notably NOT
// /assessment_report, which the motor list uses for its stat tiles. Property's
// "Total Inspections" figure comes from total_countOfClaims on the list
// response itself.
//
// Property's /assessment_list/all takes no claim_state parameter, so there is
// no status filter here (the property portal's own list page has Country +
// date range + search only), and search filters the loaded rows client-side
// since no /search endpoint is part of this flow.

const FLAGS = {
  latvia: latviaFlag,
  lv: latviaFlag,
  lat: latviaFlag,
  lithuania: lithuaniaFlag,
  lt: lithuaniaFlag,
  ltu: lithuaniaFlag,
  estonia: estoniaFlag,
  ee: estoniaFlag,
  est: estoniaFlag,
};

const getCountryFlagSrc = (countryName) => FLAGS[countryName?.toLowerCase().trim()] || null;

// Motor's Cases.js sends this lowercase and its backend accepts it. Property
// rows come back with country_corp_id spelled "Estonia"/"Lithuania" — Title
// Case, full name (see the real /assessment_list/all sample in
// propertyAssessment.test.js) — and the property API is a different backend
// from motor's, so there's no reason to assume it normalises case the same
// way. Sent here in the one form actually observed on this data.
const getCountryApiValue = (countryName) => {
  const country = countryName?.toLowerCase().trim();
  if (["latvia", "lv", "lat"].includes(country)) return "Latvia";
  if (["lithuania", "lt", "ltu"].includes(country)) return "Lithuania";
  if (["estonia", "ee", "est"].includes(country)) return "Estonia";
  return "";
};

const COUNTRY_OPTIONS = ["All", "Latvia", "Lithuania", "Estonia"];
const PAGE_SIZE = 15;

function getLast30DayRange() {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 30);
  return {
    startDate: from.toISOString().split("T")[0],
    endDate: today.toISOString().split("T")[0],
  };
}

export default function PropertyCases() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cases, setCases] = useState([]);
  const [totalInspections, setTotalInspections] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const nextOffsetRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const [selectedCountry, setSelectedCountry] = useState("All");
  const [startDate, setStartDate] = useState(() => getLast30DayRange().startDate);
  const [endDate, setEndDate] = useState(() => getLast30DayRange().endDate);
  const [searchQuery, setSearchQuery] = useState("");

  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const countryDropdownRef = useRef(null);
  const datePickerRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // The portal's list page loads these alongside the cases. Their payloads
  // aren't surfaced in the UI yet — fetched here to keep the property flow's
  // request set identical to the portal's own.
  useEffect(() => {
    Promise.allSettled([
      getPropertyClientDemo(),
      getPropertyClientConfig(),
      getPropertyUseridList(),
    ]).then((results) => {
      results.forEach((r) => {
        if (r.status === "rejected") {
          console.warn("[PropertyCases] side-load failed:", r.reason?.message);
        }
      });
    });
  }, []);

  const fetchCases = useCallback(
    async (isLoadingMoreCall = false, isSilent = false) => {
      try {
        if (!isSilent) {
          if (isLoadingMoreCall) setIsLoadingMore(true);
          else setLoading(true);
        }

        const response = await getPropertyAssessmentList({
          start_date: startDate || undefined,
          stop_date: endDate || undefined,
          per_page_limit: PAGE_SIZE,
          countryname: getCountryApiValue(selectedCountry),
          offset: isLoadingMoreCall ? nextOffsetRef.current || 0 : 0,
        });

        const { rows, total, nextOffset } = toPropertyList(response);
        nextOffsetRef.current = nextOffset;
        if (total != null) setTotalInspections(total);

        setCases((prev) => (isLoadingMoreCall ? [...prev, ...rows] : rows));
        setError(null);
      } catch (err) {
        console.error("Error fetching property cases:", err);
        setError("Failed to load property cases");
      } finally {
        if (!isSilent) {
          if (isLoadingMoreCall) setIsLoadingMore(false);
          else setLoading(false);
        }
      }
    },
    [startDate, endDate, selectedCountry],
  );

  useEffect(() => {
    nextOffsetRef.current = null;
    fetchCases(false);
  }, [fetchCases]);

  // Poll while any case is still in-process, mirroring Cases.js's 5s refresh.
  useEffect(() => {
    const hasProcessingCases = cases.some(
      (c) => getStatusInfo(parseInt(c.status, 10)).text === "In-Process",
    );
    if (hasProcessingCases) {
      pollingIntervalRef.current = setInterval(() => fetchCases(false, true), 5000);
    }
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [cases, fetchCases]);

  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        scrollHeight - (scrollTop + clientHeight) <= 200 &&
        !isLoadingMore &&
        !loading &&
        nextOffsetRef.current != null
      ) {
        fetchCases(true);
      }
    };
    const container = document.getElementById("property-cases-list-container");
    if (container) {
      scrollContainerRef.current = container;
      container.addEventListener("scroll", handleScroll, { passive: true });
    }
    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isLoadingMore, loading, fetchCases]);

  const resetFilters = () => {
    setSelectedCountry("All");
    const defaultRange = getLast30DayRange();
    setStartDate(defaultRange.startDate);
    setEndDate(defaultRange.endDate);
    setSearchQuery("");
    setShowCountryDropdown(false);
    setShowDatePicker(false);
  };

  const setDateShortcut = (days) => {
    const today = new Date();
    const pastDate = new Date();
    pastDate.setDate(today.getDate() - days);
    const fmt = (d) => d.toISOString().split("T")[0];
    setStartDate(fmt(pastDate));
    setEndDate(fmt(today));
  };

  // See filterRowsByCountry (propertyAssessment.js) for why this re-applies
  // the country filter client-side on top of the server-side countryname
  // param above.
  const countryFiltered = filterRowsByCountry(cases, selectedCountry);

  // No /search endpoint in the property flow — filter the loaded rows on
  // ref_num or address instead.
  const query = searchQuery.trim().toLowerCase();
  const filteredCases = query
    ? countryFiltered.filter(
        (c) =>
          c.ref_num?.toLowerCase().includes(query) ||
          c.address?.toLowerCase().includes(query),
      )
    : countryFiltered;

  const handleCaseClick = (caseItem) => {
    if (getStatusInfo(parseInt(caseItem.status, 10)).text === "In-Process") return;
    if (!caseItem.ref_num) return;
    // The detail response's own country (link_generated_details.country) can
    // come back empty — carry the list's own value along so the header still
    // has somewhere to read it from. Same mechanism as the redesign's list.
    window.open(
      withCountryParam(
        `/property-cases/${encodeRefNum(caseItem.ref_num)}`,
        caseItem.country,
      ),
      "_blank",
    );
  };

  if (error) {
    return (
      <div className="bg-background-primary relative h-full w-full">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => fetchCases(false)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background-primary relative h-full min-h-0 w-full overflow-hidden">
      <div className="flex h-full min-h-0 flex-col px-4 lg:px-6 xl:px-8 pb-4">
        {/* Page header. Motor fills this band with three stat cards; property
            has a single headline metric (total_countOfClaims), so the card is
            paired with a page title instead of floating alone against an
            empty strip. */}
        <div className="pt-6 mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-[22px] font-semibold leading-tight text-text-primary">
              Property Cases
            </h1>
            <p className="text-text-secondary text-sm mt-1">
              Inspection queue and property assessment reports
            </p>
          </div>
          <div className="bg-white rounded-[12px] border border-border-light px-6 py-4 shadow-sm min-w-[200px] shrink-0">
            <div className="text-[#6b7280] text-xs font-medium mb-1">Total Inspections</div>
            <div className="text-[#1f2937] text-[28px] font-bold leading-[1]">
              {loading && totalInspections == null
                ? "..."
                : (totalInspections ?? 0).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:justify-between items-start lg:items-center gap-4 mb-6">
          <div className="flex flex-wrap gap-3">
            <button
              className="bg-primary box-border flex gap-[8px] items-center justify-center overflow-clip px-[16px] py-[10px] rounded-[8px]"
              onClick={resetFilters}
            >
              <span className="font-semibold text-sm text-white">All Cases</span>
            </button>

            <div ref={countryDropdownRef} className="bg-background-primary relative rounded-[8px] border border-border-primary">
              <div
                className="box-border flex gap-[8px] items-center justify-center overflow-clip pl-[16px] pr-[8px] py-[10px] cursor-pointer"
                onClick={() => {
                  setShowCountryDropdown((v) => !v);
                  setShowDatePicker(false);
                }}
              >
                <span className="font-semibold text-text-primary text-sm">
                  {selectedCountry === "All" ? "Country" : selectedCountry}
                </span>
                <img alt="Chevron down" className="w-[16px] h-[16px]" src={chevronDownFigma} />
              </div>
              {showCountryDropdown && (
                <div className="absolute top-full bg-white border border-border-primary rounded-[8px] shadow-lg z-10 mt-1">
                  {COUNTRY_OPTIONS.map((country) => (
                    <div
                      key={country}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm flex items-center gap-2"
                      onClick={() => {
                        setSelectedCountry(country);
                        setShowCountryDropdown(false);
                      }}
                    >
                      {getCountryFlagSrc(country) && (
                        <img alt={country} className="w-4 h-4" src={getCountryFlagSrc(country)} />
                      )}
                      <span>{country}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div ref={datePickerRef} className="bg-background-primary relative rounded-[8px] border border-border-primary">
              <div
                className="box-border flex gap-[8px] items-center justify-center overflow-clip pl-[16px] pr-[8px] py-[10px] cursor-pointer"
                onClick={() => {
                  setShowDatePicker((v) => !v);
                  setShowCountryDropdown(false);
                }}
              >
                <span className="font-semibold text-text-primary text-sm">
                  {startDate && endDate ? `${startDate} - ${endDate}` : "Select Date Range"}
                </span>
                <img alt="Chevron down" className="w-[16px] h-[16px]" src={chevronDownFigma} />
              </div>
              {showDatePicker && (
                <div className="absolute top-full left-0 bg-white border border-border-primary rounded-[8px] shadow-lg z-10 mt-1 p-4 min-w-[360px]">
                  <div className="flex gap-2 mb-3">
                    <button onClick={() => setDateShortcut(1)} className="flex-1 bg-gray-100 px-2 py-1 rounded text-[11px] font-medium hover:bg-gray-200 border border-border-primary">Last Day</button>
                    <button onClick={() => setDateShortcut(7)} className="flex-1 bg-gray-100 px-2 py-1 rounded text-[11px] font-medium hover:bg-gray-200 border border-border-primary">Last 7 Days</button>
                    <button onClick={() => setDateShortcut(30)} className="flex-1 bg-gray-100 px-2 py-1 rounded text-[11px] font-medium hover:bg-gray-200 border border-border-primary">Last 30 Days</button>
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs text-text-secondary mb-1">From Date</label>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full px-3 py-2 border border-border-primary rounded text-sm" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs text-text-secondary mb-1">To Date</label>
                    <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className="w-full px-3 py-2 border border-border-primary rounded text-sm" />
                  </div>
                  <button onClick={() => setShowDatePicker(false)} className="w-full bg-primary text-white px-3 py-2 rounded text-xs font-semibold hover:bg-[#2684d1]">Apply</button>
                </div>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="bg-background-primary relative rounded-[8px] border border-border-primary w-full sm:w-[350px]">
            <div className="box-border flex gap-[8px] items-center overflow-clip px-[16px] py-[10px]">
              <img alt="Search" className="w-[20px] h-[20px]" src={searchIcon} />
              <input
                type="text"
                placeholder="Search Inspection ID or address"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent flex-1 outline-none font-semibold text-text-primary text-sm placeholder-[#7e848c] placeholder-opacity-50"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="text-text-secondary hover:text-text-primary px-1" title="Clear search">×</button>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="w-full flex-1 min-h-0 overflow-x-auto">
          <div className="flex h-full min-h-0 flex-col min-w-[800px]">
            {/* Column widths mirror the row below exactly, including the
                row's right padding, so headers stay aligned with their data. */}
            <div className="flex items-center mb-3 pb-2 pr-[16px] border-b border-border-light">
              <div className="font-medium text-text-secondary text-xs w-[80px] shrink-0 text-center">PROPERTY</div>
              <div className="font-medium text-text-secondary text-xs flex-[2] min-w-[220px] px-3">INSPECTION ID</div>
              <div className="font-medium text-text-secondary text-xs flex-1 min-w-[120px] px-3 text-center">COUNTRY</div>
              <div className="font-medium text-text-secondary text-xs w-[180px] shrink-0 px-3 text-center">DATE</div>
              <div className="font-medium text-text-secondary text-xs flex-1 min-w-[120px] px-5 text-center">STATUS</div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <img src={loadingIcon} alt="Loading" className="w-8 h-8 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading cases...</p>
                </div>
              </div>
            ) : (
              <div id="property-cases-list-container" className="flex flex-1 min-h-0 flex-col gap-2 overflow-auto pt-1 pb-2">
                {filteredCases.length > 0 ? (
                  filteredCases.map((caseItem, index) => {
                    const statusInfo = getStatusInfo(parseInt(caseItem.status, 10));
                    const isProcessing = statusInfo.text === "In-Process";
                    const flagSrc = getCountryFlagSrc(caseItem.country);
                    return (
                      <div
                        key={caseItem.ref_num || index}
                        className={`bg-background-secondary box-border flex items-center rounded-[8px] w-full pl-0 pr-[16px] py-3 transition-shadow ${isProcessing ? "opacity-50 cursor-default" : "cursor-pointer hover:shadow-md"}`}
                        style={{ minHeight: "76px" }}
                        onClick={() => handleCaseClick(caseItem)}
                      >
                        <div className="w-[80px] shrink-0 flex items-center justify-center">
                          <div className="w-10 h-10 rounded-[8px] bg-gray-100 flex items-center justify-center">
                            {caseItem.thumbnailUrl ? (
                              <img alt="" className="w-full h-full object-cover rounded-[8px]" src={caseItem.thumbnailUrl} />
                            ) : (
                              <Building2 className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col justify-center gap-[3px] px-3 flex-[2] min-w-[220px]">
                          <span className="text-sm font-medium leading-snug" style={{ color: "#1c1c1e" }}>
                            {caseItem.address || "—"}
                          </span>
                          <span className="text-xs text-text-secondary truncate">{caseItem.displayId}</span>
                        </div>
                        <div className="flex items-center justify-center px-3 flex-1 min-w-[120px]">
                          <div className="flex gap-[8px] items-center">
                            {flagSrc && <img alt={caseItem.country} className="w-[20px] h-[15px]" src={flagSrc} />}
                            <span className="text-text-primary text-sm">{caseItem.country || "—"}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-center px-3 w-[180px] shrink-0">
                          <span className="text-sm" style={{ color: "#1c1c1e" }}>
                            {formatDateTime(caseItem.createdAt) || caseItem.createdAt || "—"}
                          </span>
                        </div>
                        <div className="flex items-center justify-center px-5 flex-1 min-w-[120px] self-stretch border-l border-background-primary">
                          <div className="flex gap-[8px] items-center">
                            <img alt={statusInfo.text} className="w-[24px] h-[24px]" src={statusInfo.icon} />
                            <span className="font-medium text-sm" style={{ color: statusInfo.color }}>{statusInfo.text}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center flex-1 h-full py-20 bg-white rounded-[8px]">
                    <p className="text-text-primary font-semibold text-lg mb-2">No cases found.</p>
                    <p className="text-text-secondary text-sm px-6">
                      We couldn't find any property cases matching your current filters or search.
                    </p>
                    <button onClick={resetFilters} className="mt-4 bg-primary text-white px-8 py-3 rounded-[8px] text-base font-bold hover:bg-[#2680c7]">
                      Reset all filters
                    </button>
                  </div>
                )}

                {isLoadingMore && (
                  <div className="flex flex-col items-center justify-center py-8">
                    <img src={loadingIcon} alt="Loading" className="w-8 h-8 animate-spin mb-2" />
                    <p className="text-text-secondary text-sm">Loading more cases...</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer count — same "Showing X of Y" shape as the motor list's,
            X being what has loaded and been filtered into view so far
            (infinite scroll, so more than one page can be in it), Y the
            server's own total for the current filters. */}
        <div className="flex-shrink-0 border-t border-border-light px-1 pt-3 text-center">
          <span className="font-['Noto_Sans:Regular',_sans-serif] font-normal text-text-secondary text-sm">
            {loading
              ? "Loading…"
              : `Showing ${filteredCases.length === 0 ? 0 : 1}–${filteredCases.length}${
                  totalInspections != null ? ` of ${totalInspections.toLocaleString()}` : ""
                }`}
          </span>
        </div>
      </div>
    </div>
  );
}
